import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./PatientScreen.scss";
import PatientTable from "./PatientTable";
import { FilterObjectTypes, usePatient } from "../../hooks/usePatient";
import { Pagination } from "antd";
import Loader from "../../atoms/Loader";
import ManagePatientList from "./ManagePatientList";
import CGMFilter from "../../atoms/CGMFilter";

const PatientScreen = () => {
  const navigate = useNavigate();
  const params = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [patientListPagination, setPatientListPagination] =
    useState<FilterObjectTypes>({
      page: 1,
      limit: 10,
      total: 0,
      filter_by: { cgm_status: [] },
    });
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [debouncedFilters, setDebouncedFilters] = useState<string[]>([]);

  useEffect(() => {
    const token: any = params.token;
    localStorage.setItem("token", token);
  }, [params]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(selectedFilters);
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedFilters]);

  useEffect(() => {
    setPatientListPagination((prev: FilterObjectTypes) => ({
      ...prev,
      filter_by: { cgm_status: debouncedFilters },
      page: 1,
    }));
  }, [debouncedFilters]);

  const {
    patients,
    loading,
    totalActive,
    activeView,
    setActiveView,
    totalPatients,
  } = usePatient(searchTerm, patientListPagination);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPatientListPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleFilterChange = (values: string[]) => {
    setSelectedFilters(values);
  };

  const handleViewChange = (view: string) => {
    setActiveView(view);
    setPatientListPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const patientListPaginationChange = (page: number, pageSize: number) => {
    setPatientListPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  const redirectToPatient = (patientId: string) => {
    window.parent.postMessage(
      {
        messageType: "PATIENT_CLICKED",
        data: { patientId },
      },
      "*"
    );
  };

  return (
    <div className="patient-page">
      <div className="content-wrapper">
        <main className="main-content">
          <div
            className="page-header"
            style={{
              display: "flex",
              flexDirection: "row-reverse",
            }}
          >
            {activeView !== "favorite" && (
              <div className="page-filter">
                <div className="" style={{ width: "250px" }}>
                  <CGMFilter
                    selectedValues={selectedFilters}
                    onChange={handleFilterChange}
                    placeholder="Filter by"
                  />
                </div>
                <div className="search-filter" style={{ display: "flex" }}>
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Search patients..."
                    className="search-input"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="view-selector">
            <button
              className={`view-btn ${activeView === "call" ? "active" : ""}`}
              onClick={() => handleViewChange("call")}
            >
              Active Patients {totalActive > 0 && `(${totalActive})`}
            </button>
            <button
              className={`view-btn ${
                activeView === "inactive" ? "active" : ""
              }`}
              onClick={() => handleViewChange("inactive")}
            >
              Plan Expired Patients
            </button>
            <button
              className={`view-btn ${
                activeView === "favorite" ? "active" : ""
              }`}
              onClick={() => handleViewChange("favorite")}
            >
              Manage Patient List
            </button>
          </div>

          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem",
              }}
            >
              <Loader noBackground={true} />
            </div>
          ) : (
            <>
              {activeView !== "favorite" && (
                <PatientTable
                  patients={patients}
                  onPatientClick={(id: string) => redirectToPatient(id)}
                />
              )}

              {activeView === "favorite" && <ManagePatientList />}

              {activeView !== "favorite" && (
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    total={totalPatients}
                    current={patientListPagination.page}
                    pageSize={patientListPagination.limit}
                    onChange={patientListPaginationChange}
                    showSizeChanger={true}
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default PatientScreen;
